import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Badge, Spinner } from "@ui";
import { createConsumer } from "@rails/actioncable";
import type { Subscription, Consumer } from "@rails/actioncable";
import {
  useAgentSchemas,
  useCreateAgentSchema,
  useActivateAgentSchema,
  useCancelAgentSchema,
  useRerunAgentSchemaPreview,
  agentSchemaKeys,
  type AgentSchema,
} from "@agentic_system/api/agent-schema";
import { useAgent, useUpdateAgent } from "@agentic_system/api/agent";
import { StructuredRenderer } from "@ui/components/spec-renderer";
import { useQueryClient } from "@tanstack/react-query";
import { Send, Check, Lightbulb, ChevronDown, Pencil, AlertTriangle, X, BookOpen, Download } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { SchemaBuilderDocsDialog } from "./schema-builder-docs-dialog";
import {
  PreviewTargetSelector,
  loadTarget,
  type SelectedTarget,
} from "@agentic_system/components/preview-target-selector";

const EXAMPLE_PROMPTS = [
  { label: "Analyse candidat", prompt: "Je veux analyser un candidat a la franchise : score de compatibilite sur 100, liste de forces, liste de faiblesses, et recommandations d'actions concretes." },
  { label: "Fiche enseigne", prompt: "Genere une fiche synthetique d'enseigne avec : secteur d'activite, nombre de points de vente, investissement requis (min/max), description du concept, et 3 arguments cles pour un candidat." },
  { label: "Diagnostic emplacement", prompt: "Evalue un emplacement commercial avec : score d'attractivite sur 100, analyse de la zone de chalandise, points forts de la localisation, risques identifies, et enseignes compatibles." },
  { label: "Comparatif enseignes", prompt: "Compare plusieurs enseignes sur un meme secteur : tableau avec nom, investissement, CA moyen, nombre de franchises, et un verdict global par enseigne." },
  { label: "Rapport ville", prompt: "Produis un rapport sur une ville pour l'implantation franchise : donnees demographiques cles, dynamisme commercial, secteurs porteurs, emplacements disponibles, et score global d'opportunite." },
  { label: "Probleme / Moyens / Benefices", prompt: "La reponse doit se structurer en 3 temps : quel est le probleme identifie, quels sont les moyens a mettre en oeuvre, et quels seront les benefices attendus." },
];

type RightTab = "preview" | "spec" | "schema";
type SchemaSnapshot = {
  spec: Record<string, unknown> | null;
  data_schema: Record<string, unknown> | null;
  example: Record<string, unknown> | null;
};
type SchemaBuilderBroadcast =
  | {
      type: "snapshot";
      message?: string | null;
      spec?: Record<string, unknown> | null;
      data_schema?: Record<string, unknown> | null;
      example?: Record<string, unknown> | null;
    }
  | { type: "completed"; schema: AgentSchema }
  | { type: "error"; error?: string };

function upsertSchemaVersion(versions: AgentSchema[] | undefined, schema: AgentSchema): AgentSchema[] {
  if (!versions || versions.length === 0) return [schema];

  const nextVersions = versions.slice();
  const index = nextVersions.findIndex((version) => version.id === schema.id);

  if (index >= 0) {
    nextVersions[index] = schema;
  } else {
    nextVersions.unshift(schema);
  }

  return nextVersions;
}

function formatDuration(durationMs: number | null | undefined): string | null {
  if (durationMs == null || durationMs < 0) return null;

  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Renders a live preview from a tool-emitted snapshot — spec + example are
 * always valid JSON (written to schema.json by the agent before emission),
 * so no partial parsing is required.
 */
function SnapshotPreview({ snapshot }: { snapshot: SchemaSnapshot }) {
  if (!snapshot.spec) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Spinner />
        <p className="text-sm text-slate-500">Generation en cours...</p>
      </div>
    );
  }

  return <StructuredRenderer spec={snapshot.spec as any} data={(snapshot.example as any) ?? {}} />;
}

export function SchemaBuilderPage() {
  const { id, versionId } = useParams<{ id: string; versionId?: string }>();
  const agentId = id ? Number(id) : 0;
  const { data: agent, isLoading: agentLoading } = useAgent(agentId);
  const { data: versions, isLoading: versionsLoading } = useAgentSchemas(agentId);
  const navigate = useNavigate();

  const isLoading = agentLoading || versionsLoading;
  const goBack = () => navigate(`/system/agentic/agents/${id}`);

  // Redirect /schema → /schema/:latest_version_id
  useEffect(() => {
    if (isLoading || !versions) return;
    if (versionId) return; // Already on a version URL

    if (versions.length > 0) {
      const latest = versions[0]; // by_version returns desc
      navigate(`/system/agentic/agents/${id}/schema/${latest.id}`, { replace: true });
    }
  }, [isLoading, versions, versionId, id, navigate]);

  if (isLoading || !agent) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // No versions yet — show empty state (no redirect needed)
  const currentVersionId = versionId ? Number(versionId) : null;

  return (
    <SchemaBuilderInner
      agentId={agentId}
      agent={agent}
      versions={versions ?? []}
      currentVersionId={currentVersionId}
      onClose={goBack}
    />
  );
}

function SchemaBuilderInner({
  agentId,
  agent,
  versions,
  currentVersionId,
  onClose,
}: {
  agentId: number;
  agent: NonNullable<ReturnType<typeof useAgent>["data"]>;
  versions: AgentSchema[];
  currentVersionId: number | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateAgent = useUpdateAgent();
  const createSchema = useCreateAgentSchema(agentId);
  const activateSchema = useActivateAgentSchema(agentId);
  const cancelSchema = useCancelAgentSchema(agentId);
  const rerunPreview = useRerunAgentSchemaPreview(agentId);

  const [previewTarget, setPreviewTarget] = useState<SelectedTarget | null>(() => loadTarget());
  const [selectorOpen, setSelectorOpen] = useState(!previewTarget);

  const [input, setInput] = useState("");
  const [userQuestion, setUserQuestion] = useState(agent?.user_facing_question ?? "");
  const [questionSaved, setQuestionSaved] = useState(!!agent?.user_facing_question?.trim());
  const [activeTab, setActiveTab] = useState<RightTab>("preview");
  const [showExamples, setShowExamples] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const sortedVersions = versions.slice().sort((a, b) => a.version - b.version);
  const currentVersion = currentVersionId ? versions.find((v) => v.id === currentVersionId) : null;

  const handleDownloadPdf = useCallback(async () => {
    const el = previewRef.current;
    if (!el || !currentVersion) return;
    setExporting(true);
    try {
      // Temporarily expand the scroll container so html2canvas captures
      // full content at proper width — no cloning, styles stay intact.
      const scrollParent = el.closest(".overflow-y-auto") as HTMLElement | null;
      const saved: Record<string, string> = {};
      if (scrollParent) {
        saved.overflow = scrollParent.style.overflow;
        saved.maxHeight = scrollParent.style.maxHeight;
        saved.height = scrollParent.style.height;
        scrollParent.style.overflow = "visible";
        scrollParent.style.maxHeight = "none";
        scrollParent.style.height = "auto";
      }

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f8fafc",
        scrollY: -window.scrollY,
      });

      if (scrollParent) {
        scrollParent.style.overflow = saved.overflow;
        scrollParent.style.maxHeight = saved.maxHeight;
        scrollParent.style.height = saved.height;
      }

      const imgWidth = 190; // A4 usable width in mm (210 - 2*10 margin)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF({ unit: "mm", format: "a4" });

      // If content is taller than one page, span multiple pages
      const pageHeight = 277; // A4 usable height in mm (297 - 2*10 margin)
      let position = 10; // top margin
      let remainingHeight = imgHeight;
      const imgData = canvas.toDataURL("image/png");

      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      } else {
        while (remainingHeight > 0) {
          pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
          remainingHeight -= pageHeight;
          if (remainingHeight > 0) {
            pdf.addPage();
            position -= pageHeight;
          }
        }
      }

      pdf.save(`${agent.label}-v${currentVersion.version}.pdf`);
    } finally {
      setExporting(false);
    }
  }, [currentVersion, agent.label]);

  // Is any version currently generating?
  const generatingVersion = sortedVersions.find((v) => v.status === "pending" || v.status === "processing");
  const isGenerating = !!generatingVersion;

  // Track the schema ID to subscribe to — set immediately on mutation success,
  // before the versions query refetches.
  const [subscribeToId, setSubscribeToId] = useState<number | null>(null);

  // Latest snapshot emitted by the tool-loop (one per schema.json write)
  const [snapshot, setSnapshot] = useState<SchemaSnapshot | null>(null);

  // Derive the ID to subscribe to: explicit from mutation, or from query data
  const activeSubId = subscribeToId || generatingVersion?.id || null;

  // ActionCable: subscribe to generating version, handle chunks + completion
  const consumerRef = useRef<Consumer | null>(null);
  const subRef = useRef<Subscription<object> | null>(null);

  useEffect(() => {
    if (!activeSubId) {
      setSnapshot(null);
      return;
    }

    subRef.current?.unsubscribe();
    consumerRef.current?.disconnect();

    const consumer = createConsumer();
    consumerRef.current = consumer;

    subRef.current = consumer.subscriptions.create(
      { channel: "AgenticSystem::SchemaBuilderChannel", schema_id: activeSubId },
      {
        received(data: SchemaBuilderBroadcast) {
          if (data.type === "snapshot") {
            console.log("[SchemaBuilder] snapshot received", {
              schemaId: activeSubId,
              elements: data.spec && typeof data.spec === "object"
                ? Object.keys((data.spec as { elements?: Record<string, unknown> }).elements ?? {}).length
                : 0,
            });
            setSnapshot({
              spec: data.spec ?? null,
              data_schema: data.data_schema ?? null,
              example: data.example ?? null,
            });
            return;
          }

          if (data.type === "completed") {
            console.log("[SchemaBuilder] completed received", {
              schemaId: data.schema.id,
              version: data.schema.version,
            });
            setSnapshot(null);
            setSubscribeToId(null);
            queryClient.setQueryData(
              agentSchemaKeys.all(agentId),
              (versions: AgentSchema[] | undefined) => upsertSchemaVersion(versions, data.schema),
            );
            queryClient.invalidateQueries({ queryKey: agentSchemaKeys.all(agentId) });
            queryClient.invalidateQueries({ queryKey: ["agents", agentId] });
            subRef.current?.unsubscribe();
            consumerRef.current?.disconnect();
            return;
          }

          if (data.type === "error") {
            console.log("[SchemaBuilder] error received", {
              schemaId: activeSubId,
              error: data.error,
            });
            setSnapshot(null);
            setSubscribeToId(null);
            queryClient.setQueryData(agentSchemaKeys.all(agentId), (versions: AgentSchema[] | undefined) => {
              if (!versions) return versions;
              return versions.map((version) => (
                version.id === activeSubId
                  ? { ...version, status: "error", error: data.error || "Erreur de generation." }
                  : version
              ));
            });
            queryClient.invalidateQueries({ queryKey: agentSchemaKeys.all(agentId) });
            queryClient.invalidateQueries({ queryKey: ["agents", agentId] });
            subRef.current?.unsubscribe();
            consumerRef.current?.disconnect();
          }
        },
      },
    );

    return () => {
      subRef.current?.unsubscribe();
      consumerRef.current?.disconnect();
    };
  }, [activeSubId, agentId, queryClient]);

  // Close on Escape
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [onClose]);

  const handleSend = useCallback((override?: string) => {
    const trimmed = (override ?? input).trim();
    if (!trimmed || createSchema.isPending || isGenerating) return;

    setInput("");
    createSchema.mutate(
      {
        prompt: trimmed,
        ...(previewTarget ? { profile_id: previewTarget.id, profile_class: previewTarget.profileClass } : {}),
      },
      {
        onSuccess: (newSchema) => {
          setSubscribeToId(newSchema.id);
          navigate(`/system/agentic/agents/${agentId}/schema/${newSchema.id}`);
        },
      },
    );
  }, [input, createSchema, isGenerating, previewTarget, agentId, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const navigateToVersion = (v: AgentSchema) => {
    if (isGenerating) return; // Locked during generation
    navigate(`/system/agentic/agents/${agentId}/schema/${v.id}`);
  };

  const isBusy = isGenerating || createSchema.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Schema Builder</h2>
            <span className="text-sm text-slate-400">{agent.label}</span>
            {currentVersion?.status === "completed" && !currentVersion.active && (
              <Button
                size="sm"
                onClick={() => activateSchema.mutate(currentVersion.id)}
                loading={activateSchema.isPending}
                icon={Check}
              >
                Activer v{currentVersion.version}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDocsOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              <BookOpen className="size-4" aria-hidden="true" />
              Documentation
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md text-slate-400 hover:text-slate-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-default-600"
            >
              <span className="sr-only">Close</span>
              <X className="size-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 relative">
        {/* Target selector overlay */}
        {selectorOpen && (
          <PreviewTargetSelector
            onSelect={(target) => { setPreviewTarget(target); setSelectorOpen(false); }}
            onCancel={previewTarget ? () => setSelectorOpen(false) : undefined}
          />
        )}

        {/* Left panel — Conversation (version history) */}
        <div className="flex-1 basis-0 border-r border-default-200 flex flex-col min-w-0 min-h-0 relative">
          <div className="flex-shrink-0 h-14 px-6 border-b border-default-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Conversation</h3>
              <p className="text-xs text-slate-500">Chaque message cree une version.</p>
            </div>
            <div className="flex items-center gap-2">
              {previewTarget && (
                <button
                  type="button"
                  onClick={() => setSelectorOpen(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-default-100 text-default-700 hover:bg-default-200 transition-colors"
                >
                  {previewTarget.name}
                  <Pencil className="w-3 h-3 text-default-400" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowExamples(!showExamples)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              >
                <Lightbulb className="w-3 h-3" />
                Exemples
                <ChevronDown className={`w-3 h-3 transition-transform ${showExamples ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>


          {/* Version list as conversation */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
            {sortedVersions.length === 0 && !isBusy && (
              <div className="text-center py-8">
                <p className="text-sm text-slate-400">Decrivez le format de reponse souhaite.</p>
                <p className="text-xs text-slate-300 mt-1">Utilisez le menu "Exemples" ci-dessous pour demarrer.</p>
              </div>
            )}

            {sortedVersions.map((v) => {
              const isSelected = v.id === currentVersionId;
              const canClick = !isGenerating && v.status === "completed";

              return (
                <div key={v.id} className="space-y-2">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-default-600 text-white">
                      {v.prompt}
                    </div>
                  </div>

                  {/* Assistant response */}
                  {v.status === "completed" && (
                    <div className="flex justify-start">
                      <button
                        type="button"
                        onClick={() => canClick && navigateToVersion(v)}
                        disabled={!canClick}
                        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm text-left transition-colors ${
                          isSelected
                            ? "bg-default-100 ring-2 ring-default-400"
                            : canClick
                              ? "bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer"
                              : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }`}
                      >
                        <Badge label={`v${v.version}`} color={v.active ? "green" : isSelected ? "purple" : "blue"} size="sm" />
                        {" "}Schema genere — {Object.keys(v.schema?.properties ?? {}).length} champs{formatDuration(v.duration_ms) ? ` · ${formatDuration(v.duration_ms)}` : ""}.
                      </button>
                    </div>
                  )}

                  {v.status === "error" && (
                    <div className="flex justify-start">
                      <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-red-50 text-red-700 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{v.error || "Erreur de generation."}</span>
                      </div>
                    </div>
                  )}

                  {(v.status === "pending" || v.status === "processing") && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-2">
                        <Spinner />
                        <span className="text-sm text-slate-500">Generation en cours...</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Examples dropdown — anchored under the top-header Exemples button */}
          {showExamples && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowExamples(false)} />
              <div className="absolute left-6 right-6 top-16 z-20 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <p className="text-sm font-semibold text-slate-700">Exemples de prompts</p>
                </div>
                <div className="max-h-[28rem] overflow-y-auto divide-y divide-slate-100">
                  {EXAMPLE_PROMPTS.map((ex) => (
                    <button
                      key={ex.label}
                      type="button"
                      onClick={() => { setShowExamples(false); handleSend(ex.prompt); }}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50/70 transition-colors"
                    >
                      <span className="text-sm font-medium text-slate-700">{ex.label}</span>
                      <p className="text-xs text-slate-400 mt-0.5">{ex.prompt}</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Footer — Input only */}
          <div className="flex-shrink-0 border-t border-default-200 h-[5.5rem] flex flex-col justify-end">
            <div className="px-6 pb-3 flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
                placeholder="Decrivez le format de reponse..."
                disabled={isBusy || !questionSaved}
              />
              <Button
                icon={Send}
                onClick={() => handleSend()}
                disabled={!input.trim() || isBusy || !questionSaved}
                loading={isBusy}
              />
            </div>
          </div>
        </div>

        {/* Right panel — Shows the selected version */}
        <div className="flex-1 basis-0 flex flex-col min-w-0 min-h-0">
          <div className="flex-shrink-0 h-14 px-6 border-b border-default-200 flex items-center">
            <div className="flex items-center justify-between w-full">
              <div className="flex gap-1">
                {(["preview", "spec", "schema"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      activeTab === tab ? "bg-default-600 text-white" : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {tab === "preview" ? "Preview" : tab === "spec" ? "Render Spec" : "Data Schema"}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {currentVersion?.status === "completed" && activeTab === "preview" && (
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={exporting}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-50"
                  >
                    <Download className="size-3.5" aria-hidden="true" />
                    {exporting ? "Export..." : "PDF"}
                  </button>
                )}
                {currentVersion && (
                  <Badge
                    label={`v${currentVersion.version}`}
                    color={currentVersion.active ? "green" : "blue"}
                    size="sm"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0 bg-slate-50/70">
            {/* Generating state — show snapshot preview or spinner */}
            {currentVersion && (currentVersion.status === "pending" || currentVersion.status === "processing") && (
              snapshot?.spec ? (
                <div>
                  <SnapshotPreview snapshot={snapshot} />
                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="white"
                      size="sm"
                      onClick={() => cancelSchema.mutate(currentVersion.id)}
                      loading={cancelSchema.isPending}
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Spinner />
                  <p className="text-sm text-slate-500">Generation en cours...</p>
                  <Button
                    variant="white"
                    size="sm"
                    onClick={() => cancelSchema.mutate(currentVersion.id)}
                    loading={cancelSchema.isPending}
                  >
                    Annuler
                  </Button>
                </div>
              )
            )}

            {/* Error state */}
            {currentVersion?.status === "error" && (
              <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{currentVersion.error || "Erreur de generation."}</p>
              </div>
            )}

            {/* Completed state */}
            {currentVersion?.status === "completed" && (
              <>
                {activeTab === "preview" && (
                  <div ref={previewRef}>
                    {currentVersion.render_spec && currentVersion.example && Object.keys(currentVersion.example).length > 0 ? (
                      <StructuredRenderer spec={currentVersion.render_spec as any} data={currentVersion.example} />
                    ) : (
                      <p className="text-sm text-slate-400 italic text-center py-8">Aucune preview disponible.</p>
                    )}
                  </div>
                )}

                {activeTab === "spec" && (
                  <div>
                    {currentVersion.render_spec ? (
                      <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap">
                        {JSON.stringify(currentVersion.render_spec, null, 2)}
                      </pre>
                    ) : (
                      <p className="text-sm text-slate-400 italic text-center py-8">Le render spec apparaitra ici.</p>
                    )}
                  </div>
                )}

                {activeTab === "schema" && (
                  <div>
                    {currentVersion.schema && Object.keys(currentVersion.schema).length > 0 ? (
                      <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap">
                        {JSON.stringify(currentVersion.schema, null, 2)}
                      </pre>
                    ) : (
                      <p className="text-sm text-slate-400 italic text-center py-8">Le data schema apparaitra ici.</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* No version selected */}
            {!currentVersion && (
              <p className="text-sm text-slate-400 italic text-center py-8">
                Decrivez le resultat souhaite pour voir la preview.
              </p>
            )}
          </div>

          {/* Fake user input — simulates the end-user's message that produced the preview above.
              Editable inline; saving persists to agent.user_facing_question AND re-runs the
              agent preview (step 2 only) on the currently-selected completed version, if any. */}
          {questionSaved && userQuestion && (
            <div className="flex-shrink-0 border-t border-default-200 bg-white h-[5.5rem] flex flex-col justify-end">
              <div className="px-6 pb-3 flex gap-2">
                <textarea
                  value={userQuestion}
                  onChange={(e) => setUserQuestion(e.target.value)}
                  rows={2}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
                  placeholder="Question que l'utilisateur poserait..."
                  disabled={isBusy}
                />
                <Button
                  icon={Check}
                  variant="white"
                  onClick={() => {
                    const trimmed = userQuestion.trim();
                    if (!trimmed) return;
                    updateAgent.mutate({ id: agentId, input: { user_facing_question: trimmed } });
                    if (currentVersion?.status === "completed") {
                      rerunPreview.mutate({
                        schemaId: currentVersion.id,
                        ...(previewTarget ? { profile_id: previewTarget.id, profile_class: previewTarget.profileClass } : {}),
                      });
                    }
                  }}
                  disabled={!userQuestion.trim() || isBusy || updateAgent.isPending || rerunPreview.isPending}
                  loading={updateAgent.isPending || rerunPreview.isPending}
                  title="Enregistrer et relancer l'exemple"
                />
              </div>
            </div>
          )}
        </div>

        {/* Question overlay */}
        {!questionSaved && (
          <div className="absolute inset-0 z-30 bg-white/90 backdrop-blur-sm flex items-center justify-center">
            <div className="w-full max-w-md px-6 space-y-4">
              <div className="text-center">
                <h4 className="text-base font-semibold text-slate-700">Question utilisateur</h4>
                <p className="text-sm text-slate-500 mt-1">Cette question sera envoyee a l'agent lors de chaque execution.</p>
              </div>
              <input
                type="text"
                value={userQuestion}
                onChange={(e) => setUserQuestion(e.target.value)}
                placeholder="ex: Analyse ce profil et donne-moi ton avis."
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && userQuestion.trim()) {
                    updateAgent.mutate({ id: agentId, input: { user_facing_question: userQuestion.trim() } });
                    setQuestionSaved(true);
                  }
                }}
              />
              <Button
                onClick={() => {
                  if (!userQuestion.trim()) return;
                  updateAgent.mutate({ id: agentId, input: { user_facing_question: userQuestion.trim() } });
                  setQuestionSaved(true);
                }}
                disabled={!userQuestion.trim()}
                className="w-full"
              >
                Enregistrer et continuer
              </Button>
            </div>
          </div>
        )}
      </div>

      <SchemaBuilderDocsDialog open={docsOpen} onClose={() => setDocsOpen(false)} />
    </div>
  );
}
